import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { switchMap } from "rxjs/operators";
import { AppService } from "../../core/services/app.service";
import { LoadingService } from "./loading.service";

@Injectable({
  providedIn: "root",
})
export class UploadObsService {
  constructor(
    public http: HttpClient,
    private loadingService: LoadingService
  ) {}

  private getPresignedUrl(folder, fileType): Observable<{ presignedURL: any; url: any }> {
    return this.http
      .get<{ url: string }>(AppService.getApiUrl() + `/presignedURL?folder=${folder}&fileType=${fileType}`)
      .pipe(
        switchMap((response: any) => {
          if (!response || !response.Data || !response.Data.presignedURL || !response.Data.url) {
            throw new Error("Presigned URL not found");
          }
          return [
            {
              presignedURL: response.Data.presignedURL,
              url: response.Data.url,
            },
          ];
        })
      );
  }

  private getFileTypeForUpload(file: File): string {
    const type = file.type.toLowerCase();
    if (type && type !== "application/octet-stream") {
      return file.type;
    }
    if (/\.(heic|heif)$/i.test(file.name)) {
      return "image/heic";
    }
    return type || "application/octet-stream";
  }

  uploadFileToS3(folder: string, selectedFile: File, maxSize: number = 50): Observable<any> {
    if (!selectedFile || !folder) {
      throw new Error("Invalid params");
    }

    if (selectedFile.size / 1024 / 1024 >= maxSize) {
      throw new Error("The max size upload is " + maxSize + " MB");
    }

    const fileType = this.getFileTypeForUpload(selectedFile);
    return this.getPresignedUrl(folder, fileType).pipe(
      switchMap(({ presignedURL, url }) => {
        return new Observable(observer => {
          this.loadingService.start();
          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener("progress", event => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100);
              observer.next({ type: "progress", percent: percentComplete });
            }
          });

          xhr.addEventListener("load", () => {
            observer.next({ type: "complete", url });
            observer.complete();
            this.loadingService.complete();
          });

          xhr.addEventListener("error", error => {
            observer.error(error);
            this.loadingService.complete();
          });

          xhr.addEventListener("abort", () => {
            observer.error(new Error("Upload cancelled"));
            this.loadingService.complete();
          });

          xhr.open("PUT", presignedURL);
          xhr.setRequestHeader("Content-Type", fileType);
          xhr.send(selectedFile);

          // Return teardown function to abort XHR when unsubscribed
          return () => {
            if (xhr.readyState !== XMLHttpRequest.DONE) {
              try {
                xhr.abort();
              } catch (error) {
                console.warn("Failed to abort XHR:", error);
              }
            }
            this.loadingService.complete();
          };
        });
      })
    );
  }

  uploadPhotoToS3(folder: string, selectedFile: File, maxSize = 50): Observable<any> {
    if (!selectedFile || !folder) {
      throw new Error("Invalid params");
    }

    if (selectedFile.size / 1024 / 1024 >= maxSize) {
      throw new Error("The max size upload is " + maxSize + " MB");
    }

    const fileType = this.getFileTypeForUpload(selectedFile);
    return this.getPresignedUrl(folder, fileType).pipe(
      switchMap(({ presignedURL, url }) => {
        return new Observable(observer => {
          this.loadingService.start();
          const xhr = new XMLHttpRequest();

          xhr.addEventListener("load", () => {
            observer.next({ type: "complete", url });
            observer.complete();
            this.loadingService.complete();
          });

          xhr.addEventListener("error", error => {
            observer.error(error);
            this.loadingService.complete();
          });

          xhr.open("PUT", presignedURL);
          xhr.setRequestHeader("Content-Type", fileType);
          xhr.send(selectedFile);
        });
      })
    );
  }

  uploadVideoToS3(folder: string, selectedFile: File, maxSize = 50): Observable<any> {
    if (!selectedFile || !folder) {
      throw new Error("Invalid params");
    }

    if (selectedFile.size / 1024 / 1024 >= maxSize) {
      throw new Error("The max size upload is " + maxSize + " MB");
    }

    const fileType = this.getFileTypeForUpload(selectedFile);
    return this.getPresignedUrl(folder, fileType).pipe(
      switchMap(({ presignedURL, url }) => {
        return new Observable(observer => {
          this.loadingService.start();
          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener("progress", event => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100);
              observer.next({ type: "progress", percent: percentComplete });
            }
          });

          xhr.addEventListener("load", () => {
            observer.next({ type: "complete", url });
            observer.complete();
            this.loadingService.complete();
          });

          xhr.addEventListener("error", error => {
            observer.error(error);
            this.loadingService.complete();
          });

          xhr.open("PUT", presignedURL);
          xhr.setRequestHeader("Content-Type", fileType);
          xhr.send(selectedFile);
        });
      })
    );
  }
}
